/**
 * The `nav` Alpine component — behavior for the header chrome.
 *
 * Follows the extracted-component convention (.claude/instructions/
 * interactivity.md): the header Twig root carries x-data="nav()" and
 * directives reference methods here. State lives as data-attributes on the
 * server-rendered markup (data-open) plus imperative aria-* updates, so the
 * chrome CSS (resources/css/chrome.css, scoped to html.js) shows the right
 * pieces and no-JS visitors keep the complete, navigable menu.
 *
 * Desktop: each dropdown parent has its own <a> plus an adjacent disclosure
 * <button>. Click/focus opens; hover opens too (additive, as the prototype).
 * One submenu open at a time; Escape and outside-click close and return
 * focus to the button.
 *
 * Mobile: the hamburger opens a full-screen overlay (scroll locked, focus
 * trapped, Escape closes and returns focus). Parents open drill-down panels:
 * `openPath` is the stack of open items, outermost first, up to three levels.
 * Back pops one panel; Escape pops a panel or closes the overlay.
 *
 * The layout switch is `data-nav` on <html>: `measure()` reads the bar's
 * natural width in the wide layout and `fit()` writes "compact" when it would
 * not fit (Stafford's measured breakpoint). A head script seeds the attribute
 * from the CSS breakpoint before the first paint.
 *
 * Registers inside `alpine:init`; app.js imports this module and owns
 * Alpine.start().
 */
import Alpine from 'alpinejs';

const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

document.addEventListener( 'alpine:init', () => {
	Alpine.data( 'nav', () => ( {
		/** [data-nav-item] elements, document order (level 1 and level 2). */
		items: [],

		/** Open items, outermost first. [] = none. */
		openPath: [],

		/** Mobile overlay open state. */
		mobileOpen: false,

		/** The wide bar's natural width in px; 0 = not measured yet. */
		required: 0,

		init() {
			this.items = Array.from( this.$root.querySelectorAll( '[data-nav-item]' ) );

			this.onDocumentKeydown = ( event ) => {
				if ( 'Escape' === event.key ) {
					this.onEscape();
				} else if ( 'Tab' === event.key && this.mobileOpen ) {
					this.trapFocus( event );
				}
			};
			this.onDocumentClick = ( event ) => {
				if ( ! this.$root.contains( event.target ) ) {
					this.closeSubmenus();
				}
			};
			// Only a mobile/desktop switch resets state: phones fire resize when the address bar collapses.
			this.wasMobile = this.isMobile();
			// Every resize frame compares against the cached measurement (cheap, so the
			// switch is immediate); the measurement itself refreshes once the resize settles.
			this.onResize = () => {
				if ( ! this.frame ) {
					this.frame = requestAnimationFrame( () => {
						this.frame = 0;
						this.fit();
					} );
				}

				clearTimeout( this.resizeTimer );
				this.resizeTimer = setTimeout( () => this.fit( true ), 150 );
			};
			this.onLoad = () => this.fit( true );

			document.addEventListener( 'keydown', this.onDocumentKeydown );
			document.addEventListener( 'click', this.onDocumentClick );
			window.addEventListener( 'resize', this.onResize );
			window.addEventListener( 'load', this.onLoad );
			document.fonts?.ready.then( () => this.fit( true ) );

			this.syncSubmenus();
			this.syncMobile();
			this.$nextTick( () => this.fit( true ) );
		},

		/** Compact layout: `data-nav` on <html> (seeded by the head script, kept by fit()). */
		isMobile() {
			return 'compact' === document.documentElement.dataset.nav;
		},

		/** The wide bar's natural width, measured with the wide layout applied for one frame. */
		measure() {
			const root = document.documentElement;
			const row = this.$root;
			const previous = { nav: root.dataset.nav, width: row.style.width, maxWidth: row.style.maxWidth };

			root.dataset.nav = 'wide';
			row.style.width = 'max-content';
			row.style.maxWidth = 'none';

			this.required = row.getBoundingClientRect().width;

			row.style.width = previous.width;
			row.style.maxWidth = previous.maxWidth;
			root.dataset.nav = previous.nav || 'compact';
		},

		/** Write data-nav from the measurement; a layout switch resets the open state. */
		fit( remeasure = false ) {
			if ( remeasure || ! this.required ) {
				this.measure();
			}

			const wasMobile = this.isMobile();
			const mobile = this.required > this.$root.clientWidth + 1;

			document.documentElement.dataset.nav = mobile ? 'compact' : 'wide';

			if ( mobile === wasMobile ) {
				return;
			}

			this.closeSubmenus();

			if ( this.mobileOpen && ! mobile ) {
				this.closeMobile();
			}
		},

		disclosure( item ) {
			return item.querySelector( ':scope > [data-nav-disclosure]' );
		},

		/** The item's ancestor items, outermost first, then the item. */
		pathTo( item ) {
			const path = [ item ];
			let parent = item.parentElement?.closest( '[data-nav-item]' );

			while ( parent ) {
				path.unshift( parent );
				parent = parent.parentElement?.closest( '[data-nav-item]' );
			}

			return path;
		},

		// ── Submenus: desktop dropdowns and mobile panels ────────────────────

		/** x-on:click on each disclosure button. */
		toggleSubmenu( event ) {
			const button = event.target.closest( '[data-nav-disclosure]' );

			if ( ! button ) {
				return;
			}

			const item = button.closest( '[data-nav-item]' );
			const open = this.openPath[ this.openPath.length - 1 ] === item;

			this.openPath = open ? this.openPath.slice( 0, -1 ) : this.pathTo( item );
			this.syncSubmenus();
		},

		/** x-on:click on a panel's Back button: pop one panel, focus its disclosure. */
		back() {
			const item = this.openPath[ this.openPath.length - 1 ];

			if ( ! item ) {
				return;
			}

			this.openPath = this.openPath.slice( 0, -1 );
			this.syncSubmenus();
			this.disclosure( item )?.focus();
		},

		/**
		 * x-on:click on a parent's link. On a touch device at desktop width
		 * (no hover, the chevron clipped) the first tap opens the submenu; the
		 * second tap follows the link.
		 */
		openOnTouch( event ) {
			const item = event.currentTarget.closest( '[data-nav-item]' );

			if ( this.isMobile() || ! window.matchMedia( '(hover: none)' ).matches || ! this.disclosure( item ) || this.openPath[ 0 ] === item ) {
				return;
			}

			event.preventDefault();
			this.openPath = [ item ];
			this.syncSubmenus();
		},

		/** x-on:mouseenter on a top-level item (desktop only). */
		openOnHover( event ) {
			const item = event.currentTarget;

			if ( this.isMobile() || ! this.disclosure( item ) ) {
				return;
			}

			this.openPath = [ item ];
			this.syncSubmenus();
		},

		/** x-on:mouseleave on a top-level item (desktop only). */
		closeOnHover( event ) {
			if ( this.isMobile() || this.openPath[ 0 ] !== event.currentTarget ) {
				return;
			}

			this.closeSubmenus();
		},

		closeSubmenus() {
			if ( 0 === this.openPath.length ) {
				return;
			}

			this.openPath = [];
			this.syncSubmenus();
		},

		syncSubmenus() {
			this.items.forEach( ( item ) => {
				const button = this.disclosure( item );

				if ( ! button ) {
					return;
				}

				const isOpen = this.openPath.includes( item );

				item.dataset.open = isOpen ? 'true' : 'false';
				button.setAttribute( 'aria-expanded', isOpen ? 'true' : 'false' );
			} );
		},

		// ── Mobile overlay ───────────────────────────────────────────────────

		/** x-on:click on the hamburger button. */
		toggleMobile() {
			if ( this.mobileOpen ) {
				this.closeMobile();

				return;
			}

			this.mobileOpen = true;
			this.closeSubmenus();
			this.syncMobile();
			this.$root.querySelector( '[data-nav-panel] [data-nav-close]' )?.focus();
		},

		/** x-on:click on any close button. */
		closeMobile() {
			if ( ! this.mobileOpen ) {
				return;
			}

			this.mobileOpen = false;
			this.closeSubmenus();
			this.syncMobile();
			this.$root.querySelector( '[data-nav-hamburger]' )?.focus();
		},

		syncMobile() {
			const hamburger = this.$root.querySelector( '[data-nav-hamburger]' );
			const panel = this.$root.querySelector( '[data-nav-panel]' );

			hamburger?.setAttribute( 'aria-expanded', this.mobileOpen ? 'true' : 'false' );

			if ( panel ) {
				panel.dataset.open = this.mobileOpen ? 'true' : 'false';
			}

			const root = document.documentElement;

			if ( this.mobileOpen ) {
				// Measured before the lock removes the scrollbar; chrome.css pads by it.
				root.style.setProperty( '--og-scrollbar-w', `${ window.innerWidth - root.clientWidth }px` );
				root.style.overflow = 'hidden';
			} else {
				root.style.overflow = '';
				root.style.removeProperty( '--og-scrollbar-w' );
			}
		},

		/** The open panel: the deepest open item's submenu, or the root panel. */
		activePanel() {
			const item = this.openPath[ this.openPath.length - 1 ];

			return item?.querySelector( ':scope > [data-nav-submenu]' ) || this.$root.querySelector( '[data-nav-panel]' );
		},

		/** Keep Tab inside the open panel while the overlay is up. */
		trapFocus( event ) {
			const panel = this.activePanel();
			const stops = Array.from( panel.querySelectorAll( FOCUSABLE ) ).filter(
				( element ) => element.getClientRects().length > 0 && element.closest( '[data-nav-submenu]' ) === ( panel.matches( '[data-nav-submenu]' ) ? panel : null )
			);

			if ( 0 === stops.length ) {
				return;
			}

			const first = stops[ 0 ];
			const last = stops[ stops.length - 1 ];

			if ( event.shiftKey && document.activeElement === first ) {
				event.preventDefault();
				last.focus();
			} else if ( ! event.shiftKey && document.activeElement === last ) {
				event.preventDefault();
				first.focus();
			} else if ( ! panel.contains( document.activeElement ) ) {
				event.preventDefault();
				first.focus();
			}
		},

		// ── Escape: close the innermost thing and return focus ───────────────

		onEscape() {
			if ( this.mobileOpen ) {
				if ( this.openPath.length > 0 ) {
					this.back();
				} else {
					this.closeMobile();
				}

				return;
			}

			if ( this.openPath.length > 0 ) {
				const button = this.disclosure( this.openPath[ 0 ] );

				this.closeSubmenus();
				button?.focus();
			}
		},
	} ) );
} );
